import { AnswerDataService, BulkSolutionCreateDto } from './answerDataService';
import { TestMetadataService } from './testMetadataService';
import { TestMetadata } from '../../types/testMetadata';
import { apiClient } from '../api/client';

export interface BulkSyncOptions {
  bulkSubmitUrl: string;
  accessToken?: string;
  tenantId: string;
  onProgress?: (progress: number, message: string) => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

export class BulkSyncService {
  /**
   * Submit test answers to MSSQL backend
   */
  static async submitTestAnswers(
    testId: string,
    tenantId: string,
    bulkSubmitUrl: string,
    accessToken?: string
  ): Promise<void> {
    try {
      // Get bulk submission data
      const bulkData = await AnswerDataService.completeTest(testId, tenantId);
      
      if (!bulkData) {
        throw new Error('No data to submit');
      }
      
      // Submit to MSSQL endpoint
      const response = await fetch(bulkSubmitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
          'Abp.TenantId': tenantId
        },
        body: JSON.stringify(bulkData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Submission failed: ${response.status} - ${errorText}`);
      }
      
      // Mark as synced
      const metadata = await TestMetadataService.getTestMetadata(testId, tenantId);
      if (metadata) {
        await AnswerDataService.markAnswersAsSynced(
          testId,
          metadata.userId.toString(),
          tenantId,
          true
        );
      }
    } catch (error) {
      // Mark sync as failed
      const metadata = await TestMetadataService.getTestMetadata(testId, tenantId);
      if (metadata) {
        await AnswerDataService.markAnswersAsSynced(
          testId,
          metadata.userId.toString(),
          tenantId,
          false,
          error instanceof Error ? error.message : 'Unknown error'
        );
      }
      throw error;
    }
  }
  
  /**
   * Retry failed submissions for a tenant
   */
  static async retryFailedSubmissions(
    tenantId: string,
    accessToken?: string,
    options?: {
      maxRetries?: number;
      onProgress?: (progress: number, message: string) => void;
      onError?: (testId: string, error: Error) => void;
      onSuccess?: (testId: string) => void;
    }
  ): Promise<{ succeeded: string[]; failed: string[] }> {
    const failedTests = await AnswerDataService.getFailedSyncTests(tenantId);
    const results = { succeeded: [] as string[], failed: [] as string[] };
    
    if (failedTests.length === 0) {
      options?.onProgress?.(100, 'No failed submissions to retry');
      return results;
    }
    
    let processed = 0;
    for (const test of failedTests) {
      try {
        if (test.bulkSubmitUrl && test.bulkSubmitEnabled) {
          await this.submitTestAnswers(
            test.testId,
            tenantId,
            test.bulkSubmitUrl,
            accessToken
          );
          results.succeeded.push(test.testId);
          options?.onSuccess?.(test.testId);
        } else {
          // Skip tests without bulk submit configuration
          results.failed.push(test.testId);
        }
      } catch (error) {
        results.failed.push(test.testId);
        options?.onError?.(test.testId, error as Error);
      }
      
      processed++;
      const progress = Math.round((processed / failedTests.length) * 100);
      options?.onProgress?.(
        progress,
        `Processed ${processed} of ${failedTests.length} tests`
      );
    }
    
    return results;
  }
  
  /**
   * Check if test has pending sync
   */
  static async hasPendingSync(
    testId: string,
    tenantId: string
  ): Promise<boolean> {
    const metadata = await TestMetadataService.getTestMetadata(testId, tenantId);
    return metadata ? 
      metadata.status === 'completed' && !metadata.syncedToMssql : 
      false;
  }
  
  /**
   * Get sync status for a test
   */
  static async getSyncStatus(
    testId: string,
    tenantId: string
  ): Promise<{
    status: 'not_started' | 'in_progress' | 'completed' | 'synced' | 'sync_failed';
    syncedToMssql: boolean;
    syncAttempts: number;
    lastSyncAttempt?: Date;
    syncError?: string;
  }> {
    const metadata = await TestMetadataService.getTestMetadata(testId, tenantId);
    
    if (!metadata) {
      return {
        status: 'not_started',
        syncedToMssql: false,
        syncAttempts: 0
      };
    }
    
    return {
      status: metadata.status || 'not_started' as const,
      syncedToMssql: metadata.syncedToMssql || false,
      syncAttempts: metadata.syncAttempts || 0,
      lastSyncAttempt: metadata.lastSyncAttempt?.toDate(),
      syncError: metadata.syncError || undefined
    };
  }
}